import { useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
// hooks
import { useActions } from "../../../../common/hooks/useActions";
// actions
import { initialFrankfurtCardPassenger, frankfurtCardActionCreators } from "../frankfurtCardModule";
// helpers
import { getUpdatedArray } from "../../../../common/utils/helpers/getUpdatedArray";
import { getPassengerFieldErrors } from "../helpers/getPassengerFieldErrors";
import { FRANKFURT_CARD_MAX_PASSENGERS } from "../helpers/validateFrankfurtCardSeePrice";
import { type FrankfurtCardPassenger } from "../types";
// components
import {
  PassengerAddButton,
  PassengerColumnHeaders,
  PassengerFormShell,
  PassengerNameFields,
  PassengerRemoveButton,
  PassengerRowMobileDivider
} from "../../shared/passengers";

export default function FrankfurtCardPassengersForm() {
  const { t } = useTranslation();

  const passengerList = useSelector((state: any) => state.frankfurtCard.passengerList) as FrankfurtCardPassenger[];
  const showValidationErrors = useSelector((state: any) => state.frankfurtCard.showValidationErrors);
  const prebookingError = useSelector((state: any) => state.frankfurtCard.prebookingError);

  const updatePassengers = useActions(frankfurtCardActionCreators.updatePassengersAction, []);
  const clearPrebookingError = useActions(frankfurtCardActionCreators.clearPrebookingErrorAction, []);

  const { byPassenger: passengerFieldErrors } = useMemo(
    () => getPassengerFieldErrors(prebookingError, passengerList.length),
    [prebookingError, passengerList.length]
  );

  // A passenger change makes passenger field errors stale; "See price" validates again.
  const clearStalePrebookingError = () => {
    if (Object.keys(passengerFieldErrors).length > 0) {
      clearPrebookingError();
    }
  };

  const handlePassengerFirstNameChange = (event: any, index: number) => {
    updatePassengers(getUpdatedArray(passengerList, index, "firstName", event.target.value));
    clearStalePrebookingError();
  };

  const handlePassengerLastNameChange = (event: any, index: number) => {
    updatePassengers(getUpdatedArray(passengerList, index, "lastName", event.target.value));
    clearStalePrebookingError();
  };

  const removePassenger = (index: number) => {
    updatePassengers(passengerList.filter((_, i) => i !== index));
    clearStalePrebookingError();
  };

  const addPassengerToList = () => {
    if (passengerList.length >= FRANKFURT_CARD_MAX_PASSENGERS) {
      return;
    }

    updatePassengers([...passengerList, { ...initialFrankfurtCardPassenger, hasRemove: true }]);
    clearStalePrebookingError();
  };

  return (
    <PassengerFormShell
      title={`${t("transport.passengers")} *`}
      columnHeaders={
        <PassengerColumnHeaders>
          <Grid item xs={5} className="passengers-subtitle" pb={1}>
            {t("transport.formFirstName")}
          </Grid>
          <Grid item xs={5} className="passengers-subtitle" pb={1}>
            {t("transport.formLastName")}
          </Grid>
        </PassengerColumnHeaders>
      }
      addButton={
        passengerList.length < FRANKFURT_CARD_MAX_PASSENGERS ? (
          <PassengerAddButton onClick={addPassengerToList} />
        ) : null
      }
      infoText={
        <Box data-cy="frankfurt-card-passengers-note">
          <p>{t("transport.frankfurtCard.passengersAgeNote")}</p>
          <p>{t("transport.frankfurtCard.passengersValidityNote")}</p>
        </Box>
      }
    >
      {passengerList?.length > 0 &&
        passengerList.map((passenger, index) => {
          const shouldHighlightFirstName = showValidationErrors && (passenger?.firstName?.length ?? 0) < 2;
          const shouldHighlightLastName = showValidationErrors && (passenger?.lastName?.length ?? 0) < 2;
          const fieldErrors = passengerFieldErrors[index];
          const fieldErrorMessages = [fieldErrors?.firstName, fieldErrors?.lastName].filter(Boolean);

          return (
            <Grid container columnSpacing={{ xs: 1 }} mb={2} key={index}>
              <PassengerNameFields
                firstName={passenger.firstName}
                lastName={passenger.lastName}
                firstNameField="firstName"
                lastNameField="lastName"
                md={5}
                onFirstNameChange={(event: any) => handlePassengerFirstNameChange(event, index)}
                onLastNameChange={(event: any) => handlePassengerLastNameChange(event, index)}
                firstNameError={shouldHighlightFirstName || !!fieldErrors?.firstName}
                lastNameError={shouldHighlightLastName || !!fieldErrors?.lastName}
              />
              <Grid
                item
                md={2}
                sm={12}
                xs={12}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  ...(passenger?.hasRemove ? {} : { opacity: 0, pointerEvents: "none" })
                }}
              >
                <PassengerRemoveButton onClick={() => removePassenger(index)} />
              </Grid>
              {fieldErrorMessages.length > 0 && (
                <Grid item xs={12} data-cy={`frankfurt-card-passenger-errors-${index}`}>
                  {fieldErrorMessages.map((message, messageIndex) => (
                    <div key={messageIndex} className="frankfurt-card__field-error">
                      {message}
                    </div>
                  ))}
                </Grid>
              )}
              <PassengerRowMobileDivider />
            </Grid>
          );
        })}
    </PassengerFormShell>
  );
}
